import React, { useEffect, useMemo, useRef, useState } from 'react';
import './_NewRiderPackage.css';
import { RidersModel } from '../models/RidersModel';
import {
    RiderPackageModel,
    RiderPackageServiceModel,
    RiderServiceModel,
    ManufacturerModel,
    ActivityStatus,
    RiderServiceBranch,
    YesNo,
} from '../models/superbit-api-models';
import { DraftEditor } from '../tasks/_DraftEditor';

// React port of Views/SystemManagement/Riders/_NewRiderPackage.cshtml plus the
// addRiderPackageBtn / openRiderPackage submit logic from wwwroot/js/systemManagement.js.
// Handles BOTH add (packageId undefined) and edit (packageId set); both save
// through riderUpsertPackage, exactly like the old form which always posted to
// Rider/UpsertPackage. The lookups the form binds to (services, suppliers,
// companies, the manufacturer tree, vehicle-ownership list) all come back on the
// RiderPackageModel returned by riderGetPackage.

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

type Props = {
    show: boolean;
    // Existing package id when editing; undefined when adding.
    packageId?: string;
    onClose: () => void;
    onSaved: () => void;
};

// Per-service row state (one per entry in package.allServices).
type ServiceState = {
    checked: boolean;
    companyId: string;
    supplierId: string;
    itemId: string; // existing RiderPackageService.Id, or EMPTY_GUID for a new one
};

// Normalised manufacturer-tree node. A node with no children is a leaf and is the
// unit of selection (submodels normally, but also childless manufacturers/models).
type TreeNode = { id: string; label: string; children: TreeNode[] };

// VehicleOwnership2 display names (סוג הרכב) — mirrors the [Display(Name=...)]
// attributes on the C# enum; the index is the enum value.
const VEHICLE_OWNERSHIP2_OPTIONS: string[] = [
    'פרטי עד 4 טון',
    'מסחרי עד 4 טון',
    'משאית',
    'מונית',
    'אוטובוס זעיר',
    'טרקטור',
    'נתמך/נגרר',
    'אופנוע',
    'אחר',
    'רכב אספנות פרטי',
    'רכב אספנות מסחרי',
    'פרטי יבוא אישי',
    'מסחרי עד 3.5 טון',
    'ר.מדברי עד 3.5 טון',
    'אוטובוס זעיר ציבורי',
    'אוטובוס זעיר פרטי',
    'השכרה פרטי',
    'השכרה מסחרי',
    'צ.מ.ה',
    'ר.מדברי מעל 3.5 טון',
];

// --- manufacturer-tree helpers ------------------------------------------------

const buildTree = (mans: ManufacturerModel[]): TreeNode[] =>
    (mans || []).map((m) => ({
        id: m.id || '',
        label: m.name || '',
        children: (m.vehicleModels || []).map((mod) => ({
            id: mod.id || '',
            label: mod.name || '',
            children: (mod.vehicleSubModels || []).map((sm) => ({
                id: sm.id || '',
                label: sm.modelCode ? `${sm.name} - ${sm.modelCode}` : sm.name || '',
                children: [],
            })),
        })),
    }));

// Seed the selected-leaf set from the `checked` flags the server stamps on the
// tree when editing (1 = checked). A fully-selected manufacturer comes back with
// all of its submodels flagged, so seeding from the leaves reconstructs everything.
const seedSelected = (mans: ManufacturerModel[]): Set<string> => {
    const s = new Set<string>();
    (mans || []).forEach((m) => {
        const models = m.vehicleModels || [];
        if (models.length === 0) {
            if (m.checked === 1 && m.id) s.add(m.id);
            return;
        }
        models.forEach((mod) => {
            const subs = mod.vehicleSubModels || [];
            if (subs.length === 0) {
                if (mod.checked === 1 && mod.id) s.add(mod.id);
                return;
            }
            subs.forEach((sm) => {
                if (sm.checked === 1 && sm.id) s.add(sm.id);
            });
        });
    });
    return s;
};

const leafIds = (node: TreeNode): string[] =>
    node.children.length === 0 ? [node.id] : node.children.flatMap(leafIds);

const isFullySelected = (node: TreeNode, sel: Set<string>): boolean =>
    node.children.length === 0
        ? sel.has(node.id)
        : node.children.every((c) => isFullySelected(c, sel));

const isPartiallySelected = (node: TreeNode, sel: Set<string>): boolean => {
    const leaves = leafIds(node);
    const checked = leaves.filter((id) => sel.has(id)).length;
    return checked > 0 && checked < leaves.length;
};

// Minimal covering id set: emit the highest fully-selected nodes (a manufacturer
// id when all its models/submodels are selected, else a model id, else submodel
// ids) — matching what the old jQuery walker produced for VehiclesS and what the
// server's GetPackage re-expands.
const coveringIds = (nodes: TreeNode[], sel: Set<string>): string[] =>
    nodes.flatMap((node) => {
        if (isFullySelected(node, sel)) return [node.id];
        if (node.children.length === 0) return [];
        return coveringIds(node.children, sel);
    });

// Tri-state checkbox (needs the DOM `indeterminate` property, which JSX can't set).
const TriCheckbox = (props: {
    checked: boolean;
    indeterminate: boolean;
    onChange: () => void;
}) => {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = props.indeterminate && !props.checked;
    }, [props.indeterminate, props.checked]);
    return <input ref={ref} type="checkbox" checked={props.checked} onChange={props.onChange} />;
};

const NewRiderPackage = (props: Props) => {
    const { show, packageId, onClose, onSaved } = props;
    const isEdit = !!packageId;

    const [ridersModel] = useState<RidersModel>(() => new RidersModel());

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');
    const [errors, setErrors] = useState<{ [k: string]: string }>({});

    // The model returned by getPackage carries all the lookups (services,
    // suppliers, manufacturer tree). We keep it to read those lists.
    const [pkg, setPkg] = useState<RiderPackageModel | undefined>(undefined);

    // --- scalar form fields ---
    const [name, setName] = useState<string>('');
    const [riderServiceBranch, setRiderServiceBranch] = useState<RiderServiceBranch>(
        RiderServiceBranch._0
    );
    const [autoActive, setAutoActive] = useState<YesNo>(YesNo._0);
    const [status, setStatus] = useState<ActivityStatus>(ActivityStatus._0);
    const [agentCost, setAgentCost] = useState<string>('');
    const [recommendedCost, setRecommendedCost] = useState<string>('');
    const [maxVehicleAge, setMaxVehicleAge] = useState<number>(30);
    const [includeInPolicyPrice, setIncludeInPolicyPrice] = useState<boolean>(false);
    const [sideGimel, setSideGimel] = useState<boolean>(false);
    const [privateIncome, setPrivateIncome] = useState<boolean>(false);
    const [serviceReporter, setServiceReporter] = useState<string>('');
    const [comment, setComment] = useState<string>('');
    const [vehicleOwnership, setVehicleOwnership] = useState<number[]>([]);

    // --- services + vehicle tree ---
    const [serviceState, setServiceState] = useState<{ [serviceId: string]: ServiceState }>({});
    const [selectedVehicles, setSelectedVehicles] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const services: RiderServiceModel[] = pkg?.allServices || [];
    const suppliers = pkg?.allSuppliers || [];
    const tree = useMemo(() => buildTree(pkg?.vehicleDetails || []), [pkg]);

    // Company-name fallback: GetPackage includes RiderServiceCompanies but not
    // always the nested RiderComapny, so resolve the name from allCompanies by id.
    const companyNameById = useMemo(() => {
        const map: { [id: string]: string } = {};
        (pkg?.allCompanies || []).forEach((c) => {
            if (c.id) map[c.id] = c.name || '';
        });
        return map;
    }, [pkg]);

    // "all vehicles" === every manufacturer in the tree is fully selected (the
    // old form's "כל רכב" / root checkbox).
    const allVehicles = tree.length > 0 && tree.every((n) => isFullySelected(n, selectedVehicles));
    const selectedSubCount = useMemo(() => {
        // Count selected leaves for the "X sub-models" summary.
        const allLeaves = tree.flatMap(leafIds);
        return allLeaves.filter((id) => selectedVehicles.has(id)).length;
    }, [tree, selectedVehicles]);

    useEffect(() => {
        if (!show) return;
        setErrors({});
        setServerError('');
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, packageId]);

    const load = async () => {
        setLoading(true);
        try {
            const model = await ridersModel.getPackage(packageId);
            setPkg(model);

            setName(model.name || '');
            setRiderServiceBranch(model.riderServiceBranch ?? RiderServiceBranch._0);
            setAutoActive(model.autoActive ?? YesNo._0);
            setStatus(model.status ?? ActivityStatus._0);
            // The old form blanks "0.00" so the field shows empty for new packages.
            setAgentCost(model.agentCost ? String(model.agentCost) : '');
            setRecommendedCost(model.recommendedCost ? String(model.recommendedCost) : '');
            setMaxVehicleAge(model.maxVehicleAge || 30);
            setIncludeInPolicyPrice(!!model.includeInPolicyPrice);
            setSideGimel(!!model.sideGimel);
            setPrivateIncome(!!model.privateIncome);
            setServiceReporter(model.serviceReporter || '');
            setComment(model.comment || '');
            setVehicleOwnership(
                Array.isArray(model.vehicleOwnershipList) ? (model.vehicleOwnershipList as number[]) : []
            );

            // Seed each service row from any existing RiderPackageService whose
            // company belongs to that service (matching the old cshtml lookup).
            const existing = model.riderPackageServices || [];
            const initial: { [serviceId: string]: ServiceState } = {};
            (model.allServices || []).forEach((svc) => {
                const item = existing.find(
                    (x) => x.riderServiceCompany && x.riderServiceCompany.riderServiceId === svc.id
                );
                initial[svc.id || ''] = {
                    checked: !!item,
                    companyId: item?.riderServiceCompanyId || '',
                    supplierId: item?.riderServiceSupplierId || '',
                    itemId: item?.id || EMPTY_GUID,
                };
            });
            setServiceState(initial);

            // An "all vehicles" package may come back without every submodel
            // flagged, so mark every leaf in that case; otherwise reconstruct the
            // selection from the per-leaf `checked` flags the server stamps.
            if (model.allVehicles) {
                const allLeaves = buildTree(model.vehicleDetails || []).flatMap(leafIds);
                setSelectedVehicles(new Set(allLeaves));
            } else {
                setSelectedVehicles(seedSelected(model.vehicleDetails || []));
            }
            setExpanded(new Set());
        } catch (e) {
            console.error('Failed to load rider package', e);
            setServerError('טעינת החבילה נכשלה');
        } finally {
            setLoading(false);
        }
    };

    // --- vehicle tree interactions ---
    const toggleExpand = (id: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const toggleNode = (node: TreeNode) => {
        const leaves = leafIds(node);
        const fully = isFullySelected(node, selectedVehicles);
        setSelectedVehicles((prev) => {
            const next = new Set(prev);
            if (fully) leaves.forEach((id) => next.delete(id));
            else leaves.forEach((id) => next.add(id));
            return next;
        });
    };

    const toggleAllVehicles = () => {
        const allLeaves = tree.flatMap(leafIds);
        setSelectedVehicles((prev) => {
            if (allVehicles) return new Set();
            return new Set(allLeaves);
        });
    };

    // --- service row interactions ---
    const updateService = (serviceId: string, patch: Partial<ServiceState>) =>
        setServiceState((prev) => ({
            ...prev,
            [serviceId]: { ...prev[serviceId], ...patch },
        }));

    // --- validation + submit ---
    const validate = (): boolean => {
        const errs: { [k: string]: string } = {};
        if (!name.trim()) errs.name = 'שם חבילה — שדה חובה';
        if (!agentCost || parseFloat(agentCost) === 0) errs.agentCost = 'עלות לסוכן — שדה חובה';
        if (!recommendedCost || parseFloat(recommendedCost) === 0)
            errs.recommendedCost = 'עלות מומלצת לצרכן — שדה חובה';
        if (!serviceReporter || !serviceReporter.replace(/<[^>]*>/g, '').trim()) {
            errs.serviceReporter = 'דיווח שירות — שדה חובה';
        }
        // Require a vehicle selection only when there are vehicles to pick from —
        // otherwise an empty/unloaded tree would block submit with no way to clear it.
        if (tree.length > 0 && !allVehicles && selectedSubCount === 0) {
            errs.vehicles = 'יש לבחור רכבים';
        }

        const checkedServices = Object.values(serviceState).filter((s) => s.checked);
        // The old form requires at least one service only when the package is active.
        if (checkedServices.length === 0 && status === ActivityStatus._0) {
            errs.services = 'חובה לבחור שירות';
        }
        // Every checked service must have a company chosen.
        if (checkedServices.some((s) => !s.companyId)) {
            errs.services = 'יש לבחור חברה בכל שירות';
        }

        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            console.warn('[RiderPackage] validation failed:', errs);
            return false;
        }

        // cost2 (recommended) must be >= cost (agent)
        if (parseFloat(recommendedCost) < parseFloat(agentCost)) {
            setServerError('עלות לסוכן לא יכולה להיות גבוהה מעלות מומלצת לצרכן');
            console.warn('[RiderPackage] validation failed: recommendedCost < agentCost');
            return false;
        }
        return true;
    };

    const onSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
        if (e && typeof (e as any).preventDefault === 'function') e.preventDefault();
        setServerError('');
        console.log('[RiderPackage] submit clicked');
        if (!validate()) {
            console.warn('[RiderPackage] validation blocked submit', errors);
            return;
        }

        const body = new RiderPackageModel();
        body.id = packageId || EMPTY_GUID;
        body.name = name.trim();
        body.riderServiceBranch = riderServiceBranch;
        body.autoActive = autoActive;
        body.status = status;
        body.agentCost = parseFloat(agentCost);
        body.recommendedCost = parseFloat(recommendedCost);
        body.maxVehicleAge = maxVehicleAge;
        body.includeInPolicyPrice = includeInPolicyPrice;
        body.sideGimel = sideGimel;
        body.privateIncome = privateIncome;
        body.serviceReporter = serviceReporter;
        body.comment = comment;
        body.vehicleOwnershipList = vehicleOwnership as any;

        // Services: one entry per checked row (matching the old serialize loop).
        body.riderPackageServices = Object.entries(serviceState)
            .filter(([, s]) => s.checked)
            .map(([, s]) => {
                const svc = new RiderPackageServiceModel();
                svc.id = s.itemId || EMPTY_GUID;
                svc.riderServiceCompanyId = s.companyId;
                svc.riderServiceSupplierId = s.supplierId || undefined;
                return svc;
            });

        // Vehicles: "all" flag, or the minimal covering id list as VehiclesS
        // (the server splits it on "," and rebuilds RiderPackageVehicles).
        if (allVehicles) {
            body.allVehicles = true;
            body.vehiclesS = undefined;
        } else {
            body.allVehicles = false;
            body.vehiclesS = coveringIds(tree, selectedVehicles).filter(Boolean).join(',');
        }

        setSaving(true);
        try {
            console.log('[RiderPackage] calling upsertPackage', body);
            await ridersModel.upsertPackage(body);
            console.log('[RiderPackage] upsertPackage succeeded');
            onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save rider package', err);
            setServerError('שמירת החבילה נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const renderTreeNodes = (nodes: TreeNode[]): React.ReactNode => (
        <ul>
            {nodes.map((node) => {
                const hasChildren = node.children.length > 0;
                const isOpen = expanded.has(node.id);
                return (
                    <li key={node.id}>
                        <div className="node-row">
                            <span
                                className={`caret ${hasChildren ? '' : 'leaf'}`}
                                onClick={() => hasChildren && toggleExpand(node.id)}
                            >
                                {hasChildren ? (isOpen ? '▼' : '◄') : ''}
                            </span>
                            <TriCheckbox
                                checked={isFullySelected(node, selectedVehicles)}
                                indeterminate={isPartiallySelected(node, selectedVehicles)}
                                onChange={() => toggleNode(node)}
                            />
                            <span className="node-label" onClick={() => toggleNode(node)}>
                                {node.label}
                            </span>
                        </div>
                        {hasChildren && isOpen && renderTreeNodes(node.children)}
                    </li>
                );
            })}
        </ul>
    );

    const vehicleSummary = allVehicles
        ? 'כל רכב'
        : `סה"כ: ${selectedSubCount} תתי דגם`;

    return (
        <div className="riderpkg-overlay" onClick={handleOverlayClick}>
            <div className="riderpkg-modal" dir="rtl">
                <form id="addRiderPackageForm" autoComplete="off" onSubmit={onSubmit}>
                    <div className="header">
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span className="close-modal" />
                        </button>
                        <h3 className="title">{isEdit ? 'עריכת חבילה Rider' : 'חבילה Rider'}</h3>
                    </div>

                    {loading ? (
                        <div className="riderpkg-loading">טוען…</div>
                    ) : (
                        <>
                            {/* Validation / server-error summary — shown at the top so a
                                blocked submit is obvious, not just small inline red text. */}
                            {(serverError || Object.keys(errors).length > 0) && (
                                <div className="riderpkg-error-banner">
                                    {serverError || 'יש להשלים את שדות החובה המסומנים'}
                                    {Object.keys(errors).length > 0 && (
                                        <ul>
                                            {Array.from(new Set(Object.values(errors))).map((msg) => (
                                                <li key={msg}>{msg}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {/* Row 1: name / branch / auto-add */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">שם חבילה</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                    {errors.name && <span className="text-danger">{errors.name}</span>}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">ענף</span>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={riderServiceBranch}
                                        onChange={(e) =>
                                            setRiderServiceBranch(parseInt(e.target.value, 10) as RiderServiceBranch)
                                        }
                                    >
                                        <option value={RiderServiceBranch._0}>רכב</option>
                                        <option value={RiderServiceBranch._1}>דירה</option>
                                    </select>
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">הוספה אוטומטית</span>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={autoActive}
                                        onChange={(e) => setAutoActive(parseInt(e.target.value, 10) as YesNo)}
                                    >
                                        <option value={YesNo._0}>כן</option>
                                        <option value={YesNo._1}>לא</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: status / agent cost / recommended cost */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סטטוס</span>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={status}
                                        onChange={(e) =>
                                            setStatus(parseInt(e.target.value, 10) as ActivityStatus)
                                        }
                                    >
                                        <option value={ActivityStatus._0}>פעיל</option>
                                        <option value={ActivityStatus._1}>לא פעיל</option>
                                    </select>
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">עלות לסוכן</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        type="number"
                                        value={agentCost}
                                        onChange={(e) => setAgentCost(e.target.value)}
                                    />
                                    {errors.agentCost && (
                                        <span className="text-danger">{errors.agentCost}</span>
                                    )}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">עלות מומלצת לצרכן</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        type="number"
                                        value={recommendedCost}
                                        onChange={(e) => setRecommendedCost(e.target.value)}
                                    />
                                    {errors.recommendedCost && (
                                        <span className="text-danger">{errors.recommendedCost}</span>
                                    )}
                                </div>
                            </div>

                            {/* Row 3: vehicle age / manufacturer tree / vehicle type */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">גיל הרכב</span>
                                    </div>
                                    <select
                                        className="form-control"
                                        value={maxVehicleAge}
                                        onChange={(e) => setMaxVehicleAge(parseInt(e.target.value, 10))}
                                    >
                                        {Array.from({ length: 30 }, (_, i) => 30 - i).map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-sm riderpkg-vehicle-selector">
                                    <div className="minor-title-box text-right">
                                        <span className="title">יצרן</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        readOnly
                                        value={vehicleSummary}
                                        onClick={() => toggleExpand('__root__')}
                                    />
                                    {expanded.has('__root__') && (
                                        <div className="riderpkg-tree">
                                            <ul>
                                                <li>
                                                    <div className="node-row">
                                                        <span className="caret leaf" />
                                                        <input
                                                            type="checkbox"
                                                            checked={allVehicles}
                                                            onChange={toggleAllVehicles}
                                                        />
                                                        <span
                                                            className="node-label"
                                                            onClick={toggleAllVehicles}
                                                        >
                                                            הכל
                                                        </span>
                                                    </div>
                                                    {renderTreeNodes(tree)}
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                    {errors.vehicles && (
                                        <span className="text-danger">{errors.vehicles}</span>
                                    )}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סוג הרכב</span>
                                    </div>
                                    <select
                                        multiple
                                        className="form-control"
                                        value={vehicleOwnership.map(String)}
                                        onChange={(e) =>
                                            setVehicleOwnership(
                                                Array.from(e.target.selectedOptions).map((o) =>
                                                    parseInt(o.value, 10)
                                                )
                                            )
                                        }
                                    >
                                        {VEHICLE_OWNERSHIP2_OPTIONS.map((label, idx) => (
                                            <option key={idx} value={idx}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Flags */}
                            <div className="riderpkg-flags">
                                <label className="riderpkg-check">
                                    <input
                                        type="checkbox"
                                        checked={includeInPolicyPrice}
                                        onChange={(e) => setIncludeInPolicyPrice(e.target.checked)}
                                    />
                                    <span className="title">כלול במחיר הפוליסה</span>
                                </label>
                                <label className="riderpkg-check">
                                    <input
                                        type="checkbox"
                                        checked={sideGimel}
                                        onChange={(e) => setSideGimel(e.target.checked)}
                                    />
                                    <span className="title">צד ג</span>
                                </label>
                                <label className="riderpkg-check">
                                    <input
                                        type="checkbox"
                                        checked={privateIncome}
                                        onChange={(e) => setPrivateIncome(e.target.checked)}
                                    />
                                    <span className="title">יבוא אישי</span>
                                </label>
                            </div>

                            {/* Services: checkbox + company + supplier per service */}
                            <div className="riderpkg-services">
                                {services.map((svc) => {
                                    const st = serviceState[svc.id || ''] || {
                                        checked: false,
                                        companyId: '',
                                        supplierId: '',
                                        itemId: EMPTY_GUID,
                                    };
                                    const id = svc.id || '';
                                    return (
                                        <div className="riderpkg-service-row" key={id}>
                                            <label className="service-name">
                                                <input
                                                    type="checkbox"
                                                    checked={st.checked}
                                                    onChange={(e) =>
                                                        updateService(id, { checked: e.target.checked })
                                                    }
                                                />
                                                <span>{svc.title}</span>
                                            </label>
                                            <div className="service-select">
                                                <select
                                                    className="form-control"
                                                    value={st.companyId}
                                                    onChange={(e) =>
                                                        updateService(id, { companyId: e.target.value })
                                                    }
                                                >
                                                    <option value="">חברה</option>
                                                    {(svc.riderServiceCompanies || []).map((c) => (
                                                        <option key={c.id} value={c.id}>
                                                            {(c.riderComapny && c.riderComapny.name) ||
                                                                companyNameById[c.riderComapnyId || ''] ||
                                                                ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="service-select">
                                                <select
                                                    className="form-control"
                                                    value={st.supplierId}
                                                    onChange={(e) =>
                                                        updateService(id, { supplierId: e.target.value })
                                                    }
                                                >
                                                    <option value="">ספק</option>
                                                    {suppliers.map((s) => (
                                                        <option key={s.id} value={s.id}>
                                                            {s.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    );
                                })}
                                {errors.services && (
                                    <span className="text-danger">{errors.services}</span>
                                )}
                            </div>

                            {/* Service reporter (rich text) */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">דיווח שירות</span>
                                    </div>
                                    <DraftEditor
                                        initialHtml={serviceReporter}
                                        onHtmlChange={(html) => setServiceReporter(html)}
                                    />
                                    {errors.serviceReporter && (
                                        <span className="text-danger">{errors.serviceReporter}</span>
                                    )}
                                </div>
                            </div>

                            {/* Comment (rich text, same editor as דיווח שירות) */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">הערה</span>
                                    </div>
                                    <DraftEditor
                                        initialHtml={comment}
                                        onHtmlChange={(html) => setComment(html)}
                                    />
                                </div>
                            </div>

                            {/* Red error box right next to the buttons, so a blocked
                                submit is visible without scrolling back to the top. */}
                            {(serverError || Object.keys(errors).length > 0) && (
                                <div className="riderpkg-error-box">
                                    <strong>{serverError || 'לא ניתן לשמור — יש להשלים את השדות הבאים:'}</strong>
                                    {Object.keys(errors).length > 0 && (
                                        <ul>
                                            {Array.from(new Set(Object.values(errors))).map((msg) => (
                                                <li key={msg}>{msg}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            <div className="action-buttons">
                                <div className="action-buttons__half">
                                    <button
                                        type="button"
                                        className="btn btn-link button btn-primary button-outlined"
                                        disabled={saving}
                                        onClick={onSubmit}
                                    >
                                        {saving ? 'שומר…' : isEdit ? 'שמור' : 'הוסף'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link button button-outlined"
                                        onClick={onClose}
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default NewRiderPackage;
